export interface RouteEntry {
  method: string;
  path: string;
  token?: "company" | "admin" | "master";
  requiresCompanyId?: boolean;
  /**
   * When `"lambda"`, calls go through Lambda Invoke in the cloud (and local
   * HTTP discovery using `functionKey`). Defaults to mesh HTTP.
   */
  transport?: "http" | "lambda";
  /**
   * For `transport: "lambda"`: discovery key / cloud name stem
   * (`aws-describe-tool` → local discovery + `{stage}-aws-describe-tool`).
   */
  functionKey?: string;
  /** Route display name (`docs.name` or registry key); ignored by createBrowserClient. */
  name?: string;
  /** When true, AI/tool surfaces should skip this route; ignored by createBrowserClient. */
  disableForAI?: boolean;
  /** Route docs; ignored by createBrowserClient. */
  description?: string;
  /** Route permission gate (when set on the HTTP route); ignored by createBrowserClient. */
  requiredPermission?: string | string[];
  /** JSON Schema for the request body/query; ignored by createBrowserClient. */
  inputSchema?: unknown;
  /** JSON Schema for the response; ignored by createBrowserClient. */
  outputSchema?: unknown;
}

export type RouteMap = Record<string, RouteEntry>;

export type ResponseType = "json" | "text" | "stream" | "raw";

export interface RequestPayload {
  body?: unknown;
  query?: Record<string, unknown>;
  params?: Record<string, string>;
}

export function interpolatePath(
  path: string,
  params: Record<string, string> | undefined,
): string {
  if (!params) return path;
  return path.replace(/:(\w+)/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter: ${key}`);
    }
    return encodeURIComponent(value);
  });
}

function toQueryValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return value.toString();
  }
  return JSON.stringify(value);
}

export function buildQueryString(
  query: Record<string, unknown> | undefined,
): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        for (const v of value) params.append(key, toQueryValue(v));
      } else {
        params.set(key, toQueryValue(value));
      }
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export interface HttpErrorDetails {
  status: number;
  body: string;
  method: string;
  path: string;
  service: string;
  routeName: string;
}

export class HttpError extends Error {
  readonly status: number;
  readonly body: string;
  readonly method: string;
  readonly path: string;
  readonly service: string;
  readonly routeName: string;

  constructor(message: string, details: HttpErrorDetails) {
    super(message);
    this.name = "HttpError";
    this.status = details.status;
    this.body = details.body;
    this.method = details.method;
    this.path = details.path;
    this.service = details.service;
    this.routeName = details.routeName;
  }
}

export type RetryableError = Error & {
  status?: number;
  statusCode?: number;
};

export interface RetryConfig {
  retries: number;
  shouldRetry?: (error: RetryableError) => boolean;
  executeBeforeRetry?: (
    error: RetryableError,
    attempt: number,
  ) => Promise<void>;
}

// Without this a hung request never settles and the caller (e.g. a dashboard
// widget) spins on "loading" forever. The timeout forces a rejection so the UI
// can surface a retryable error state. Opt-in via `timeoutMs` so long-lived
// server-to-server and streaming calls are never aborted mid-flight.
export const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;

export interface SendRequestOptions {
  url: string;
  method: string;
  headers: Record<string, string> | (() => Record<string, string>);
  body?: unknown;
  responseType?: ResponseType;
  fetchOptions?: Omit<RequestInit, "method" | "headers" | "body">;
  formatError: (status: number, errorBody: string, headers: Headers) => Error;
  retry?: RetryConfig;
  timeoutMs?: number;
}

function resolveHeaders(
  headers: Record<string, string> | (() => Record<string, string>),
): Record<string, string> {
  return typeof headers === "function" ? headers() : headers;
}

function resolveSignal(
  options: SendRequestOptions,
): AbortSignal | null | undefined {
  const external = options.fetchOptions?.signal;
  if (
    options.timeoutMs == null ||
    options.responseType === "stream" ||
    options.responseType === "raw"
  ) {
    return external;
  }
  const timeoutSignal = AbortSignal.timeout(options.timeoutMs);
  return external ? AbortSignal.any([external, timeoutSignal]) : timeoutSignal;
}

// `fetch` rejects with a bare "fetch failed" TypeError for every transport
// problem (DNS, refused connection, reset socket, TLS) and hides the detail in
// `cause`. Callers such as the job scheduler persist and display `message`
// verbatim, so fold the target and the root cause into it.
function describeTransportFailure(error: unknown): string {
  let cause: unknown = error;
  while (cause instanceof Error && cause.cause != null) {
    cause = cause.cause;
  }
  if (!(cause instanceof Error)) return String(cause);

  const code = (cause as { code?: unknown }).code;
  return typeof code === "string" && !cause.message.includes(code)
    ? `${cause.message} (${code})`
    : cause.message;
}

async function doFetch(options: SendRequestOptions): Promise<unknown> {
  const hasBody = options.method !== "GET" && options.method !== "DELETE";
  const request: RequestInit = {
    ...options.fetchOptions,
    method: options.method,
    headers: resolveHeaders(options.headers),
    body: hasBody && options.body ? JSON.stringify(options.body) : undefined,
    signal: resolveSignal(options),
  };

  let response: Response;
  try {
    response = await fetch(options.url, request);
  } catch (error) {
    // Aborts and timeouts stay untouched so callers can still detect them by
    // name (React Query cancellation, stream teardown).
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.name === "TimeoutError")
    ) {
      throw error;
    }
    throw new Error(
      `${options.method} ${options.url} failed: ${describeTransportFailure(error)}`,
      { cause: error },
    );
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw options.formatError(response.status, errorBody, response.headers);
  }

  if (response.status === 204) return undefined;

  switch (options.responseType) {
    case "text":
      return response.text();
    case "stream":
      return response.body;
    case "raw":
      return response;
    default:
      return response.json();
  }
}

export async function sendRequest(
  options: SendRequestOptions,
): Promise<unknown> {
  const { retry } = options;
  if (!retry) return doFetch(options);

  let lastError: unknown;
  for (let attempt = 0; attempt <= retry.retries; attempt++) {
    try {
      return await doFetch(options);
    } catch (error) {
      lastError = error;
      if (
        !(error instanceof Error) ||
        (!("status" in error) && !("statusCode" in error))
      ) {
        throw error;
      }
      const retryableError = error as RetryableError;
      if (retry.shouldRetry?.(retryableError) === false) throw error;
      if (attempt === retry.retries) throw error;
      await retry.executeBeforeRetry?.(retryableError, attempt + 1);
    }
  }
  throw lastError;
}

export interface RouteContext {
  serviceName: string;
  routeName: string;
}

export function createProxyClient<TEntry extends { routeMap: RouteMap }>(
  registry: Record<string, TEntry>,
  execute: (
    entry: TEntry,
    route: RouteEntry,
    payload: RequestPayload,
    options: unknown,
    context: RouteContext,
  ) => Promise<unknown>,
): Record<string, Record<string, (...args: unknown[]) => Promise<unknown>>> {
  return new Proxy(
    {},
    {
      get(_target, serviceName: string) {
        const entry = registry[serviceName];
        if (!entry) {
          throw new Error(`Unknown service: ${serviceName}`);
        }

        return new Proxy(
          {},
          {
            get(_routeTarget, routeName: string) {
              const route = entry.routeMap[routeName];
              if (!route) {
                throw new Error(
                  `Unknown route "${routeName}" on service "${serviceName}"`,
                );
              }

              return async (
                payload: RequestPayload = {},
                options?: unknown,
              ): Promise<unknown> => {
                return execute(entry, route, payload, options, {
                  serviceName,
                  routeName,
                });
              };
            },
          },
        );
      },
    },
  );
}
